import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FilePreviewCard } from '@components/Checkout/steps/PhoneNumberStep/FilePreviewCard';
import {
  ACCEPTED_BILL_MIME_TYPES,
  fileToPreviewViewModel,
  serverFileToPreviewViewModel,
  validatePhoneBillFile,
} from '@components/Checkout/steps/PhoneNumberStep/utils/phoneNumberStepUtils';
import type { PhoneBillUploadProps } from '@components/Checkout/steps/PhoneNumberStep/PhoneNumberStep.types';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';

const DESCRIPTION = 'Upload a recent copy of your phone bill with your current service provider.';
const ACCEPT_ATTRIBUTE = ACCEPTED_BILL_MIME_TYPES.join(',');

export const PhoneBillUpload = ({
  files,
  existingFiles,
  error,
  touched,
  onUpload,
  onRemoveLocal,
  onRemoveExisting,
}: PhoneBillUploadProps) => {
  const [clientError, setClientError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const result = validatePhoneBillFile(file);
    if (!result.ok) {
      setClientError(result.message);
      return;
    }

    setClientError(null);
    onUpload(file);
  };

  const displayedError = clientError ?? (touched && error ? error : null);
  const showError = !!displayedError;
  const hasAnyFile = existingFiles.length > 0 || files.length > 0;

  return (
    <Stack gap="10px">
      <Stack>
        <TextFieldLabel label="Phone Bill" />
        <Typography variant="body.sm" className="PhoneBillUpload-description">
          {DESCRIPTION}
        </Typography>
      </Stack>

      <Button component="label" variant="outlined">
        Upload phone bill
        <input type="file" hidden accept={ACCEPT_ATTRIBUTE} onChange={handleFileChange} />
      </Button>

      {showError && <FormErrorMessage>{displayedError}</FormErrorMessage>}

      {hasAnyFile && (
        <Stack direction="row" gap="12px" flexWrap="wrap">
          {existingFiles.map((entry) => {
            const view = serverFileToPreviewViewModel(entry);
            return <FilePreviewCard key={view.key} view={view} onRemove={() => onRemoveExisting(entry.fileId)} />;
          })}
          {files.map((file, index) => {
            const view = fileToPreviewViewModel(file);
            return <FilePreviewCard key={view.key} view={view} onRemove={() => onRemoveLocal(index)} />;
          })}
        </Stack>
      )}
    </Stack>
  );
};
